import { useEffect, useRef, type RefObject } from 'react';

// The layer stack: every open Popover / Menu / Modal / pinned Explainer registers itself, and ONE document
// keydown listener sends Escape to the top-most layer only (DESIGN_SYSTEM §13.3: "Esc closes the top-most
// layer"). Outside-click works per layer: a pointerdown outside the layer's own nodes closes it.

interface Layer {
  onEscape: () => void;
}

const stack: Layer[] = [];
let listening = false;

function onDocumentKeyDown(event: KeyboardEvent) {
  if (event.key !== 'Escape' || event.defaultPrevented) return;
  const top = stack[stack.length - 1];
  if (!top) return;
  event.preventDefault();
  top.onEscape();
}

function push(layer: Layer) {
  stack.push(layer);
  if (!listening) {
    document.addEventListener('keydown', onDocumentKeyDown);
    listening = true;
  }
}

function remove(layer: Layer) {
  const index = stack.indexOf(layer);
  if (index >= 0) stack.splice(index, 1);
  if (stack.length === 0 && listening) {
    document.removeEventListener('keydown', onDocumentKeyDown);
    listening = false;
  }
}

/** How many layers are open — for tests and DEV assertions. */
export function openLayerCount(): number {
  return stack.length;
}

/** While `active`, Escape anywhere in the document calls `onEscape` — but only if this is the top layer. */
export function useEscapeLayer(active: boolean, onEscape: () => void): void {
  const callback = useRef(onEscape);
  callback.current = onEscape;
  useEffect(() => {
    if (!active) return;
    const layer: Layer = { onEscape: () => callback.current() };
    push(layer);
    return () => remove(layer);
  }, [active]);
}

/**
 * While `active`, a pointerdown outside every element in `inside` calls `onOutside`. The trigger belongs in
 * `inside` so clicking it again toggles (the caller's click handler runs) instead of closing-then-reopening.
 */
export function useOutsideClick(
  active: boolean,
  inside: readonly RefObject<HTMLElement | null>[],
  onOutside: (event: PointerEvent) => void,
): void {
  const callback = useRef(onOutside);
  callback.current = onOutside;
  const refs = useRef(inside);
  refs.current = inside;
  useEffect(() => {
    if (!active) return;
    const listener = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      for (const ref of refs.current) {
        if (ref.current?.contains(target)) return;
      }
      callback.current(event);
    };
    document.addEventListener('pointerdown', listener);
    return () => document.removeEventListener('pointerdown', listener);
  }, [active]);
}
